import { ErrorBoundary } from "@sentry/react-native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { add, eachDayOfInterval, parse } from "date-fns";
import { useAtomValue } from "jotai";
import { min } from "lodash";
import { Suspense, useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { CardScroller } from "@whoosh/design-system/src/components/display";
import { HStack } from "@whoosh/design-system/src/components/primitive";
import { convertDateTimeObjToServerStringFormat } from "@whoosh/utilities/src/dates";

import { startDateFilterAtom, timeOfDayFilterAtom } from "../../../../atoms";
import { AvailabilityListError } from "./AvailabilityListError";
import { AvailabilityListSkeleton } from "./AvailabilityListSkeleton";
import { AvailableDatesCard } from "./AvailableDatesCard";
import { NoAvailabilityCard } from "./NoAvailabilityCard";
import { AvailableDatesListQuery } from "./__generated__/AvailableDatesListQuery.graphql";
import { AvailableDate, useGetDatesListData } from "./useGetDatesListData";

const MAX_QUERY_DAYS = 14;

type AvailableDateCard = {
  id: string;
  date: string;
  dateString: string;
  endDate?: string;
  endDateString?: string;
  instructorId: string;
  slotsCount?: number;
  durationMins?: number;
  lessonTypeId?: string;
};

type Props = {
  instructorId: string;
  facilityId: string;
  durationMins: number;
  lessonTypeId?: string;
  bookingWindowDays: number;
};

export const AvailableDatesList = ({
  instructorId,
  facilityId,
  durationMins,
  lessonTypeId,
  bookingWindowDays,
}: Props) => {
  const startDateFilterValue = useAtomValue(startDateFilterAtom);
  const startDateFilterDateObj = startDateFilterValue
    ? parse(startDateFilterValue, "yyyy-MM-dd", new Date())
    : undefined;
  const todayDateObj = useMemo(() => new Date(), []);

  const startDate = startDateFilterDateObj ?? todayDateObj;
  const endDate = add(startDate, {
    days: min([MAX_QUERY_DAYS, bookingWindowDays]),
  });

  const startDateTime = convertDateTimeObjToServerStringFormat(startDate);
  const endDateTime = convertDateTimeObjToServerStringFormat(endDate);

  const dateRange =
    endDateTime >= startDateTime
      ? eachDayOfInterval({ start: startDate, end: endDate })
      : [];

  return (
    <ErrorBoundary fallback={<AvailabilityListError />}>
      <AvailableDatesListQuerier
        key={`$instructor-date-list${instructorId}`}
        prevAvailableDates={[]}
        dateRange={dateRange}
        durationMins={durationMins}
        instructorId={instructorId}
        facilityId={facilityId}
        startDate={startDate}
        endDate={endDate}
        lessonTypeId={lessonTypeId}
        bookingWindowDays={bookingWindowDays}
      />
    </ErrorBoundary>
  );
};

type QuerierProps = {
  prevAvailableDates: AvailableDate[];
  dateRange: Date[];
  lessonTypeId?: string;
  durationMins: number;
  instructorId: string;
  facilityId: string;
  startDate: Date;
  endDate: Date;
  bookingWindowDays: number;
};

const AvailableDatesListQuerier = ({
  prevAvailableDates,
  facilityId,
  durationMins,
  instructorId,
  startDate,
  endDate,
  lessonTypeId,
  bookingWindowDays,
  dateRange,
}: QuerierProps) => {
  const [queryRef, loadQuery] = useQueryLoader<AvailableDatesListQuery>(
    AvailableDatesListQueryString
  );
  const timeOfDayFilterValue = useAtomValue(timeOfDayFilterAtom);

  const startDateTime = convertDateTimeObjToServerStringFormat(startDate);
  const endDateTime = convertDateTimeObjToServerStringFormat(endDate);

  useEffect(() => {
    loadQuery(
      {
        instructorId,
        startDateTime,
        endDateTime,
        durationMins,
        startTime: timeOfDayFilterValue?.timeGTE,
        endTime: timeOfDayFilterValue?.timeLTE,
        slotAvailability: {
          bookingType: "LESSON",
          instructorIds: [instructorId],
          lessonTypeId,
          facilityId,
        },
      },
      { fetchPolicy: "store-and-network" }
    );
  }, [
    durationMins,
    endDateTime,
    facilityId,
    lessonTypeId,
    instructorId,
    loadQuery,
    startDateTime,
    timeOfDayFilterValue?.timeGTE,
    timeOfDayFilterValue?.timeLTE,
  ]);

  return (
    <Suspense fallback={<AvailabilityListSkeleton />}>
      {queryRef ? (
        <AvailableDatesListInner
          queryRef={queryRef}
          dateRange={dateRange}
          durationMins={durationMins}
          lessonTypeId={lessonTypeId}
          instructorId={instructorId}
          prevAvailableDates={prevAvailableDates}
          bookingWindowDays={bookingWindowDays}
          facilityId={facilityId}
        />
      ) : (
        <AvailabilityListSkeleton />
      )}
    </Suspense>
  );
};

type InnerProps = {
  prevAvailableDates: AvailableDate[];
  bookingWindowDays: number;
  queryRef: PreloadedQuery<AvailableDatesListQuery>;
  dateRange: Date[];
  lessonTypeId?: string;
  durationMins: number;
  instructorId: string;
  facilityId: string;
};
const AvailableDatesListInner = ({
  bookingWindowDays,
  prevAvailableDates,
  instructorId,
  queryRef,
  dateRange,
  lessonTypeId,
  durationMins,
  facilityId,
}: InnerProps) => {
  const isMobile = useResponsiveProp({ base: 1, lg: 0 });

  const data = usePreloadedQuery(AvailableDatesListQueryString, queryRef);

  const { dateList, hasAvailability, availableDates } = useGetDatesListData({
    dateRange,
    durationMins,
    lessonTypeId,
    instructorId,
    instructorRef: data.node ?? null,
    prevAvailableDates,
  });

  dateList.push({
    id: "full-availability",
    date: "full-availability",
    dateString: "full-availability",
    instructorId,
  });

  // If we have not made enough date cards, we need to load more dates
  // so we recursively add teh AvailableDatesListQuerier until we have enough
  // cards and then we finally render the list
  if (dateList.length < 8 && dateRange.length < bookingWindowDays) {
    const remainingBookingDays = bookingWindowDays - dateRange.length;
    const nextStartDate = add(dateRange[dateRange.length - 1], {
      days: 1,
    });
    const nextEndDate = add(dateRange[dateRange.length - 1], {
      days: min([MAX_QUERY_DAYS, remainingBookingDays]),
    });

    const newDateRange =
      nextEndDate >= nextStartDate
        ? eachDayOfInterval({ start: dateRange[0], end: nextEndDate })
        : [];

    return (
      <AvailableDatesListQuerier
        key={newDateRange[newDateRange.length - 1].toString()}
        prevAvailableDates={availableDates}
        dateRange={newDateRange}
        durationMins={durationMins}
        instructorId={instructorId}
        facilityId={facilityId}
        startDate={nextStartDate}
        endDate={nextEndDate}
        lessonTypeId={lessonTypeId}
        bookingWindowDays={bookingWindowDays}
      />
    );
  }

  return hasAvailability ? (
    isMobile ? (
      <CardScroller<AvailableDateCard>
        hideMargin
        paddingHorizontal={6}
        data={dateList}
        renderItem={(item) => {
          return <AvailableDatesCard {...item} />;
        }}
      />
    ) : (
      <HStack gap={2} flexWrap="wrap" px={6} flex={1}>
        {dateList.map((date) => {
          return <AvailableDatesCard key={date.id} {...date} />;
        })}
      </HStack>
    )
  ) : (
    <NoAvailabilityCard />
  );
};

const AvailableDatesListQueryString = graphql`
  query AvailableDatesListQuery(
    $instructorId: ID!
    $startDateTime: Datetime!
    $endDateTime: Datetime!
    $durationMins: Int!
    $slotAvailability: SlotAvailabilityArgs
    $startTime: Time
    $endTime: Time
  ) {
    node(id: $instructorId) {
      ... on StaffProfile {
        ...useGetDatesListData_staffProfile
      }
    }
  }
`;
